import {
  type Artifact,
  actionPath,
  hostFetch,
  ReferenceAdapter,
  type ViewContext,
} from '@bridge/app-sdk'
import {
  AppBridge,
  PostMessageTransport,
} from '@modelcontextprotocol/ext-apps/app-bridge'
import { useEffect, useRef, useState } from 'react'

export function ArtifactView({
  bindingId,
  artifactId,
  onInspect,
  sandboxUrl,
}: {
  bindingId: string
  artifactId: string
  onInspect: (revision: number, latest?: boolean) => void
  sandboxUrl?: string
}) {
  const [artifact, setArtifact] = useState<Artifact | null>(null)
  const [error, setError] = useState('')
  const [html, setHtml] = useState('')
  const [visible, setVisible] = useState(false)
  const [fallback, setFallback] = useState(false)
  const container = useRef<HTMLDivElement>(null)
  const iframe = useRef<HTMLIFrameElement>(null)
  const inspectRef = useRef(onInspect)
  inspectRef.current = onInspect
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin: '200px' },
    )
    if (container.current) observer.observe(container.current)
    return () => observer.disconnect()
  }, [])
  useEffect(() => {
    if (!visible) return
    let stopped = false
    void (async () => {
      try {
        const saved = await hostFetch(
          `/v1/apps/bindings/${bindingId}/artifacts/${artifactId}`,
        )
        if (stopped) return
        setArtifact(saved)
        const resource = await hostFetch(
          `/v1/apps/bindings/${bindingId}/resource?uri=${encodeURIComponent(saved.resourceUri)}&artifactId=${artifactId}`,
        )
        const content = resource.contents?.[0]?.text
        if (!content) throw new Error('The saved UI resource is unavailable.')
        if (saved.resourceDigest) {
          const hash = await crypto.subtle.digest(
            'SHA-256',
            new TextEncoder().encode(content),
          )
          const digest = Array.from(new Uint8Array(hash))
            .map((v) => v.toString(16).padStart(2, '0'))
            .join('')
          if (digest !== saved.resourceDigest)
            throw new Error(
              'The saved UI version is unavailable. The original result is shown below.',
            )
        }
        if (!stopped) setHtml(content)
      } catch (e) {
        if (!stopped) {
          setError(String(e))
          setFallback(true)
        }
      }
    })()
    return () => {
      stopped = true
    }
  }, [visible, bindingId, artifactId])
  useEffect(() => {
    if (!html || !artifact || !iframe.current?.contentWindow) return
    let closed = false
    const frame = iframe.current
    const adapter = new ReferenceAdapter(bindingId, 'bound')
    let bridge: AppBridge
    let readyTimer: ReturnType<typeof setTimeout> | undefined
    void (async () => {
      try {
        const binding = await hostFetch(`/v1/apps/bindings/${bindingId}`)
        const hostSandbox = sandboxUrl ?? binding.app?.sandboxUrl
        if (
          !hostSandbox ||
          new URL(hostSandbox).origin === window.location.origin
        ) {
          throw new Error(
            'This host has no separate origin for embedded app results.',
          )
        }
        adapter.sessionId = binding.session
        adapter.capabilities.messages = !!binding.session
        const catalog = await hostFetch(
          `/v1/apps/actions?bindingId=${encodeURIComponent(bindingId)}`,
        )
        const allowedActions = new Set<string>(
          (Array.isArray(catalog)
            ? catalog
            : (catalog.actions ?? catalog.tools ?? [])
          )
            .map(
              (entry: { name?: string; id?: string; action?: string }) =>
                entry.name ?? entry.id ?? entry.action,
            )
            .filter(
              (name: unknown): name is string => typeof name === 'string',
            ),
        )
        await adapter.start(false)
        if (closed) {
          adapter.dispose()
          return
        }
        bridge = new AppBridge(
          null,
          { name: 'Canopy', version: '0.1.0' },
          {
            serverTools: {},
            serverResources: {},
            openLinks: {},
            updateModelContext: { text: {} },
            ...(binding.session ? { message: { text: {} } } : {}),
            experimental: { 'dev.agentbridge/inspection': {} },
          },
        )
        let selection: ViewContext = {
          appInstanceId: artifact.data.appInstanceId,
          greetingId: artifact.data.greeting?.id ?? null,
          selectedRevision: artifact.revision,
          currentRevision: artifact.revision,
          excerpt: artifact.data.greeting?.text ?? '',
        }
        adapter.onInspect = (revision, latest) =>
          inspectRef.current(revision, latest)
        bridge.onsandboxready = async () => {
          await bridge.sendSandboxResourceReady({
            html,
            sandbox: 'allow-scripts',
            csp: {},
          })
        }
        bridge.oninitialized = () => {
          clearTimeout(readyTimer)
          void bridge.sendToolResult({
            content: [
              {
                type: 'text',
                text: artifact.data.greeting?.text ?? 'No greeting',
              },
            ],
            structuredContent: { data: artifact.data, artifact },
          })
        }
        bridge.oncalltool = async (params) => {
          if (!allowedActions.has(params.name))
            throw new Error('This application does not expose that action.')
          const args = params.arguments ?? {}
          const result = await hostFetch(
            actionPath(bindingId, params.name),
            args,
          )
          return {
            content: [
              {
                type: 'text',
                text: result.data.greeting?.text ?? 'No greeting',
              },
            ],
            structuredContent: result,
          }
        }
        bridge.onupdatemodelcontext = async (params) => {
          if (params.structuredContent) {
            selection = params.structuredContent as ViewContext
            await adapter.context(selection)
          }
          return {}
        }
        bridge.onmessage = async (params) => {
          const text = params.content
            .filter((c) => c.type === 'text')
            .map((c) => c.text)
            .join('\n')
          const metadata = params.content.find((part) => part.type === 'text')
            ?._meta?.['dev.agentbridge/interaction']
          const interaction =
            metadata && typeof metadata === 'object' && !Array.isArray(metadata)
              ? (metadata as Record<string, unknown>)
              : undefined
          await adapter.discuss(
            text,
            selection,
            crypto.randomUUID(),
            typeof interaction?.displayText === 'string'
              ? interaction.displayText
              : undefined,
            typeof interaction?.interactionId === 'string'
              ? interaction.interactionId
              : undefined,
          )
          return {}
        }
        bridge.onopenlink = async (params) => {
          const url = new URL(params.url)
          if (
            url.origin !== 'https://bridge.invalid' ||
            url.pathname !== '/inspect'
          )
            throw new Error('Link is not an allowed inspection target')
          inspectRef.current(
            Number(url.searchParams.get('revision')),
            url.searchParams.get('latest') === 'true',
          )
          return {}
        }
        bridge.onrequestdisplaymode = async () => ({ mode: 'fullscreen' })
        bridge.onsizechange = (params) => {
          if (frame)
            frame.style.height = `${Math.min(900, Math.max(220, params.height ?? 420))}px`
        }
        bridge.onreadresource = async (params) =>
          hostFetch(
            `/v1/apps/bindings/${bindingId}/resource?uri=${encodeURIComponent(params.uri)}`,
          )
        const transport = new PostMessageTransport(
          frame.contentWindow!,
          frame.contentWindow!,
        )
        await bridge.connect(transport)
        frame.src = `${hostSandbox}/sandbox.html?parent=${encodeURIComponent(window.location.origin)}`
        readyTimer = setTimeout(() => {
          if (!closed) {
            setError(
              'Embedded app presentation is unavailable. The original saved result is shown below.',
            )
            setFallback(true)
          }
        }, 10_000)
      } catch (e) {
        if (!closed) {
          setError(String(e))
          setFallback(true)
        }
      }
    })()
    return () => {
      closed = true
      clearTimeout(readyTimer)
      adapter.dispose()
      void bridge?.close()
    }
  }, [html, artifact, bindingId, sandboxUrl])
  return (
    <div
      ref={container}
      className="artifact-view"
      data-artifact-id={artifactId}
      style={{
        margin: '12px 0',
        border: '1px solid #dce4de',
        borderRadius: 12,
        overflow: 'hidden',
        minHeight: 120,
      }}
    >
      {html && !fallback ? (
        <iframe
          ref={iframe}
          title={`Greeting snapshot revision ${artifact?.revision ?? ''}`}
          sandbox="allow-scripts allow-same-origin"
          style={{ border: 0, width: '100%', height: 430 }}
        />
      ) : (
        <div style={{ padding: 20 }}>
          <p>{artifact?.data.greeting?.text ?? 'Loading saved greeting…'}</p>
          {artifact && (
            <button
              className="btn"
              onClick={() => onInspect(artifact.revision)}
            >
              Inspect revision {artifact.revision}
            </button>
          )}
          {error && <p role="alert">{error}</p>}
        </div>
      )}
    </div>
  )
}
